import { ref, watch, toValue } from 'vue'
import { defaultPublicVariantSku, isVariantSkuPubliclyOrderable } from '../utils/publicVariantSku.js'

/**
 * Public menu: per-item variant dropdown state (option group → selected value) and resolved SKU + price.
 * @param {{ displayGroups: import('vue').Ref | (() => unknown) | import('vue').ComputedRef, variantSkus: (item: object) => any[], variantOptionGroups: (item: object) => any[] }} opts
 */
export function usePublicMenuVariantSelect(opts) {
  const { variantSkus, variantOptionGroups } = opts
  const displayGroups = opts.displayGroups

  const variantSelectByItemUuid = ref({})

  function variantSelectId(prefix, item, groupName) {
    const safe = String(groupName || 'opt')
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9_-]/g, '')
    return `${prefix}-var-${item.uuid}-${safe}`
  }

  function skuMatchesSelection(sku, sel) {
    const ov = sku?.option_values && typeof sku.option_values === 'object' ? sku.option_values : {}
    const s = sel && typeof sel === 'object' ? sel : {}
    const keys = new Set([...Object.keys(ov), ...Object.keys(s)])
    for (const k of keys) {
      if (ov[k] !== s[k]) return false
    }
    return true
  }

  function resolvedVariantSku(item) {
    const skus = variantSkus(item)
    if (!skus.length) return null
    const id = item.uuid
    const sel = variantSelectByItemUuid.value[id]
    if (!sel) return defaultPublicVariantSku(skus)
    return skus.find((s) => skuMatchesSelection(s, sel)) ?? defaultPublicVariantSku(skus)
  }

  /**
   * True if some publicly orderable SKU matches this value for the group and current selection on other groups.
   */
  function isVariantGroupValueOrderable(item, groupName, value) {
    const skus = variantSkus(item)
    const groups = variantOptionGroups(item)
    const id = item?.uuid
    if (!id || groupName == null || value === '') return true
    const sel = variantSelectByItemUuid.value[id] || {}
    return skus.some((sku) => {
      if (!isVariantSkuPubliclyOrderable(sku)) return false
      const ov = sku.option_values || {}
      if (ov[groupName] !== value) return false
      for (const g of groups) {
        const gn = g.name
        if (gn === groupName) continue
        const chosen = sel[gn]
        if (chosen != null && chosen !== '') {
          if (ov[gn] !== chosen) return false
        }
      }
      return true
    })
  }

  function variantSelectionValue(item, groupName) {
    const id = item.uuid
    const stored = variantSelectByItemUuid.value[id]?.[groupName]
    if (stored != null && stored !== '') return stored
    const g = variantOptionGroups(item).find((x) => x.name === groupName)
    const fallback = Array.isArray(g?.values) ? g.values[0] : ''
    return fallback ?? ''
  }

  function onVariantGroupChange(item, groupName, newValue) {
    const skus = variantSkus(item)
    if (!skus.length || !item.uuid) return
    const id = item.uuid
    const current = { ...(variantSelectByItemUuid.value[id] || {}) }
    current[groupName] = newValue

    const exact = skus.find((s) => skuMatchesSelection(s, current))
    if (exact) {
      variantSelectByItemUuid.value = { ...variantSelectByItemUuid.value, [id]: { ...exact.option_values } }
      return
    }

    let candidates = skus.filter((s) => (s.option_values || {})[groupName] === newValue)
    const groups = variantOptionGroups(item)
    for (const g of groups) {
      if (g.name === groupName) continue
      const v = current[g.name]
      if (v != null && v !== '') {
        candidates = candidates.filter((s) => (s.option_values || {})[g.name] === v)
      }
    }
    const pick =
      candidates[0] ||
      skus.find((s) => (s.option_values || {})[groupName] === newValue) ||
      defaultPublicVariantSku(skus)
    if (pick?.option_values) {
      variantSelectByItemUuid.value = { ...variantSelectByItemUuid.value, [id]: { ...pick.option_values } }
    }
  }

  watch(
    () => toValue(displayGroups),
    (groups) => {
      const next = { ...variantSelectByItemUuid.value }
      let changed = false
      for (const g of groups || []) {
        for (const it of g.items || []) {
          if (it.type !== 'with_variants' || !it.uuid) continue
          if (next[it.uuid]) continue
          const skus = variantSkus(it)
          const first = defaultPublicVariantSku(skus)
          if (first?.option_values && typeof first.option_values === 'object') {
            next[it.uuid] = { ...first.option_values }
            changed = true
          }
        }
      }
      if (changed) variantSelectByItemUuid.value = next
    },
    { immediate: true, deep: true }
  )

  return {
    variantSelectId,
    resolvedVariantSku,
    variantSelectionValue,
    onVariantGroupChange,
    isVariantGroupValueOrderable,
  }
}
